import { useCallback, useEffect, useState } from 'react';
import { useNotify, useRefresh } from 'react-admin';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import UndoIcon from '@mui/icons-material/Undo';
import { SectionCard } from '../../shared/Panel';
import { dateTimeNoSeconds } from '../../shared/dateTime';
import { useFeature } from '../featureFlags/FeatureGate';
import {
  fetchIntakeFlow,
  handoffPreOrder,
  returnPreOrderToCs,
  toErrorMessage,
  type IntakeFlow,
} from './intakeApi';
import { FEATURE_INTAKE_HANDOFF } from './intakeSides';
import { ContractTestId } from '../../usage/ContractTestId';

/**
 * Obieg prezlecenia CS → Administracja ze zwrotem z pytaniem (makieta back
 * office v2, fala B, B1; przełącznik `intakeHandoff`, S-21.10-03; ścieżka
 * MIX-01). Etap, licznik zwrotów, wątek pytań i odpowiedzi, przekazanie
 * (CS) i zwrot z pytaniem (Administracja). Co wolno, mówi API (`handoff`,
 * `returnToCs`); przycisk bez prawa jest wyłączony z powodem obok. Nic nie
 * wychodzi pocztą.
 */

/** Najkrótsze pytanie przy zwrocie — jak w API. */
export const MIN_RETURN_QUESTION = 10;

/** Kolor etapu: u CS — informacja, zwrot — ostrzeżenie, zatwierdzone — sukces. */
export const flowStateColor = (
  state: IntakeFlow['state'],
): 'info' | 'warning' | 'success' | 'default' =>
  state === 'returned_to_cs' ? 'warning' : state === 'approved' ? 'success' : state === 'cs' ? 'info' : 'default';

/** Obieg prezlecenia z API — tylko przy włączonym przełączniku. */
export function useIntakeFlow(preOrderId: number | string | undefined) {
  const on = useFeature(FEATURE_INTAKE_HANDOFF);
  const [flow, setFlow] = useState<IntakeFlow | null>(null);
  const [error, setError] = useState<string | null>(null);
  const reload = useCallback(async () => {
    if (!on || preOrderId === undefined) return;
    try {
      setFlow(await fetchIntakeFlow(preOrderId));
      setError(null);
    } catch (e) {
      setError(toErrorMessage(e));
    }
  }, [on, preOrderId]);
  useEffect(() => {
    void reload();
  }, [reload]);
  return { on, flow, error, reload };
}

const MessageList = ({ flow }: { flow: IntakeFlow }) =>
  flow.messages.length === 0 ? null : (
    <Stack spacing={0.75} data-testid="intake-thread">
      {flow.messages.map((m) => (
        <Box
          key={m.id}
          data-testid="intake-message"
          data-kind={m.kind}
          sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 0.75 }}
        >
          <Typography variant="caption" color="text.secondary">
            {[m.label, m.authorName, dateTimeNoSeconds(m.createdAt)].filter(Boolean).join(' · ')}
          </Typography>
          {/* Pytanie przypięte do pola (24.10.2026, `intakeQuestionField`). */}
          {m.kind === 'return' && m.fieldLabel ? (
            <Chip
              size="small"
              variant="outlined"
              color={m.field ? 'warning' : 'default'}
              label={m.field ? `pole: ${m.fieldLabel}` : 'pytanie ogólne'}
              sx={{ ml: 0.5 }}
              data-testid="intake-message-field"
            />
          ) : null}
          {m.message ? (
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {m.message}
            </Typography>
          ) : null}
        </Box>
      ))}
    </Stack>
  );

/** Wybór pola pytania: „ogólne” — wartość pusta. */
const GENERAL_FIELD = '';

/**
 * Okno przekazania albo zwrotu — pole tekstu z warunkiem długości, a przy
 * zwrocie z listą pól (24.10.2026, `intakeQuestionField`) — wybór pola,
 * którego dotyczy pytanie, albo „ogólne”.
 */
const FlowDialog = ({
  open,
  title,
  description,
  label,
  required,
  minLength,
  confirmText,
  testId,
  fields,
  onClose,
  onConfirm,
}: {
  open: boolean;
  title: string;
  description: string;
  label: string;
  required: boolean;
  minLength: number;
  confirmText: string;
  testId: string;
  /** Pola do wyboru przy pytaniu; brak — bez wyboru (pytanie ogólne). */
  fields?: ReadonlyArray<{ key: string; label: string }>;
  onClose: () => void;
  onConfirm: (text: string, field: string | null) => Promise<void>;
}) => {
  const [text, setText] = useState('');
  const [field, setField] = useState(GENERAL_FIELD);
  const [busy, setBusy] = useState(false);
  const tooShort = required && text.trim().length < minLength;
  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>{description}</DialogContentText>
        {fields && fields.length > 0 ? (
          <TextField
            select
            label="Pytanie dotyczy pola"
            fullWidth
            size="small"
            value={field}
            disabled={busy}
            onChange={(e) => setField(e.target.value)}
            helperText="Pole dostanie znacznik z pytaniem na prezleceniu, do odpowiedzi CS"
            sx={{ mb: 2 }}
            slotProps={{ htmlInput: { 'data-testid': 'intake-return-field' } }}
          >
            <MenuItem value={GENERAL_FIELD}>ogólne (bez pola)</MenuItem>
            {fields.map((f) => (
              <MenuItem key={f.key} value={f.key}>
                {f.label}
              </MenuItem>
            ))}
          </TextField>
        ) : null}
        <TextField
          label={label}
          multiline
          minRows={3}
          fullWidth
          value={text}
          disabled={busy}
          onChange={(e) => setText(e.target.value)}
          helperText={required ? `Co najmniej ${minLength} znaków` : 'Opcjonalnie'}
          slotProps={{ htmlInput: { maxLength: 2000, 'data-testid': testId } }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Anuluj
        </Button>
        <Button
          variant="contained"
          disabled={busy || tooShort}
          // Kontrakt `data-testid` paczki (S-22.10-02): zatwierdzenie okna.
          data-testid="dlg-ok"
          onClick={() =>
            void (async () => {
              setBusy(true);
              try {
                await onConfirm(text, field === GENERAL_FIELD ? null : field);
                setText('');
                setField(GENERAL_FIELD);
              } finally {
                setBusy(false);
              }
            })()
          }
        >
          {confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/**
 * Karta obiegu na prezleceniu i w oknie kontroli. `flow` z `useIntakeFlow`;
 * `onChanged` — po przekazaniu albo zwrocie (odświeżenie karty).
 */
export const IntakeFlowCard = ({
  flow,
  error,
  onChanged,
}: {
  flow: IntakeFlow | null;
  error: string | null;
  onChanged: () => void;
}) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const [dialog, setDialog] = useState<'handoff' | 'return' | null>(null);
  if (error) {
    return (
      <SectionCard title="Obieg CS → Administracja">
        <Alert severity="warning">{error}</Alert>
      </SectionCard>
    );
  }
  if (!flow) return null;

  const returned = flow.stage === 'returned_to_cs';
  const atCs = flow.stage !== 'administration';
  const isCs = flow.mySides.includes('cs');
  const isAdm = flow.mySides.includes('administration');
  const notHolder = !flow.mySides.includes(flow.holderSide);

  const done = async (action: () => Promise<unknown>, message: string) => {
    try {
      await action();
      notify(message, { type: 'success' });
      setDialog(null);
      onChanged();
      refresh();
    } catch (e) {
      notify(toErrorMessage(e), { type: 'error' });
    }
  };

  return (
    <SectionCard title="Obieg CS → Administracja">
      <Stack spacing={1} data-testid="intake-flow-card" data-stage={flow.stage} data-state={flow.state}>
        <Stack direction="row" spacing={0.5} useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
          <Chip
            size="small"
            color={flowStateColor(flow.state)}
            label={flow.stateLabel}
            data-testid="intake-flow-state"
          />
          <Chip size="small" variant="outlined" label={`dział: ${flow.department}`} />
          {flow.returnCount > 0 ? (
            <Chip
              size="small"
              variant="outlined"
              color="warning"
              label={`zwroty: ${flow.returnCount}`}
              data-testid="intake-return-count"
            />
          ) : null}
          {flow.since ? (
            <Typography variant="caption" color="text.secondary">
              od {dateTimeNoSeconds(flow.since)}
              {flow.by ? ` · ${flow.by}` : ''}
            </Typography>
          ) : null}
        </Stack>
        {notHolder ? (
          <Alert severity="info">
            Rekord jest u działu {flow.department} — widzisz podgląd, zmiany wprowadza{' '}
            {flow.department}.
          </Alert>
        ) : null}
        {/* Pytanie przypięte do pola czeka na odpowiedź CS (24.10.2026). */}
        {flow.openQuestion ? (
          <Alert severity="warning" data-testid="intake-open-question">
            {flow.openQuestion.field
              ? `Pytanie Administracji do pola „${flow.openQuestion.fieldLabel}”: `
              : 'Pytanie Administracji: '}
            {flow.openQuestion.message ?? '—'}
          </Alert>
        ) : null}
        {atCs && flow.blockers.length > 0 ? (
          <Alert severity="warning" data-testid="intake-blockers">
            Do przekazania brakuje pól, bez których importer VIA nie przyjmie pliku (wartość robocza listy pól
            obowiązkowych):
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
              {flow.blockers.slice(0, 8).map((b) => (
                <li key={b}>{b}</li>
              ))}
            </Box>
            {flow.blockers.length > 8 ? `… i ${flow.blockers.length - 8} więcej` : null}
          </Alert>
        ) : null}
        <MessageList flow={flow} />
        <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
          {isCs && atCs ? (
            // Kontrakt `data-testid` paczki (S-22.10-02): przekazanie CS → Administracja.
            <ContractTestId id="cs-zlecenie-przekaz" disabled={!flow.handoff.allowed} tip={flow.handoff.refusal ?? undefined}>
              <Button
                size="small"
                variant="contained"
                startIcon={<SendIcon />}
                disabled={!flow.handoff.allowed}
                data-testid="intake-handoff"
                onClick={() => setDialog('handoff')}
              >
                {returned ? 'Odpowiedz i przekaż ponownie' : 'Przekaż do Administracji'}
              </Button>
            </ContractTestId>
          ) : null}
          {isAdm && !atCs ? (
            // Kontrakt `data-testid` paczki (S-22.10-02): zwrot do CS z pytaniem.
            <ContractTestId id="adm-zlecenie-zwrot-cs" disabled={!flow.returnToCs.allowed} tip={flow.returnToCs.refusal ?? undefined}>
              <Button
                size="small"
                variant="outlined"
                color="warning"
                startIcon={<UndoIcon />}
                disabled={!flow.returnToCs.allowed}
                data-testid="intake-return"
                onClick={() => setDialog('return')}
              >
                Zwróć do CS z pytaniem
              </Button>
            </ContractTestId>
          ) : null}
        </Stack>
        {isCs && atCs && !flow.handoff.allowed && flow.handoff.refusal ? (
          <Typography variant="caption" color="text.secondary" data-testid="intake-handoff-refusal">
            {flow.handoff.refusal}
          </Typography>
        ) : null}
        {isAdm && !atCs && !flow.returnToCs.allowed && flow.returnToCs.refusal ? (
          <Typography variant="caption" color="text.secondary">
            {flow.returnToCs.refusal}
          </Typography>
        ) : null}
      </Stack>
      <FlowDialog
        open={dialog === 'handoff'}
        title={returned ? 'Odpowiedź dla Administracji' : 'Przekaż do Administracji'}
        description={
          returned
            ? 'Odpowiedź trafi do wątku prezlecenia, a prezlecenie wróci do kolejki Administracji. Nic nie wychodzi pocztą.'
            : 'Prezlecenie trafi do kolejki Administracji do kontroli i zapisu do VIA. Do tego czasu zmiany wprowadza Administracja albo zwraca je z pytaniem.'
        }
        label={returned ? 'Odpowiedź' : 'Uwaga dla Administracji'}
        required={returned}
        minLength={3}
        confirmText={returned ? 'Odpowiedz i przekaż' : 'Przekaż'}
        testId="intake-handoff-message"
        onClose={() => setDialog(null)}
        onConfirm={(text) =>
          done(() => handoffPreOrder(flow.preOrderId, text), 'Przekazano do Administracji')
        }
      />
      <FlowDialog
        open={dialog === 'return'}
        title="Zwróć do CS z pytaniem"
        description="Prezlecenie wróci do CS. Zatwierdzenie zapisu do VIA, jeśli było, się cofa — CS zmieni dane i przekaże ponownie z odpowiedzią. Pytanie trafi do wątku prezlecenia, nic nie wychodzi pocztą."
        label="Pytanie do CS"
        required
        minLength={MIN_RETURN_QUESTION}
        confirmText="Zwróć do CS"
        testId="intake-return-question"
        fields={flow.questionFields}
        onClose={() => setDialog(null)}
        onConfirm={(text, field) =>
          done(() => returnPreOrderToCs(flow.preOrderId, text, field), 'Zwrócono do CS z pytaniem')
        }
      />
    </SectionCard>
  );
};
